import { BCMSDate } from "@bcm-dev/bcms-vite";
import { EndedMessageWidget, ReceivedMessageWidget, SentMessageWidget } from "components/pages/ChatPage/components/ChatMessageWidget";

/**
 * @class ChatItem
 * @classdesc Class definition for chat messages in the chat
 * 
 * @property {STRING} _message - The message of the chat item
 * @property {STRING} _type - The type of the chat item
 * @property {bcmsDate} _date - The date instance
 * @property {INTEGER} _offset - The offset that should be applied to the seconds
 * @property {STRING} _messageReceiver - The receiver of the message
 */
export default class ChatItem {
    _message;
    
    _type;
    _date;
    _messageReceiver;

    constructor({ message, type, date, offset, messageReceiver }) {
        this.message = message;
        this.type = type;
        
        const dateTime = date instanceof BCMSDate ? date : BCMSDate.init();
        this.date = dateTime.offsetSeconds(offset ?? 0).format("h:i:sp").toLowerCase();
        this.messageReceiver = messageReceiver;
    }

    /**
     * @name message
     * @instance
     * @property set {STRING} - Sets the message of the item
     * @memberof ChatItem
     */
    set message(value) { this._message = value; }
    /**
     * @name type
     * @instance
     * @property set {STRING} - Sets the type of the item
     * @memberof ChatItem
     */
    set type(value) { this._type = value; }
    /**
     * @name date
     * @instance
     * @property set {STRING} - Sets the date of the item
     * @memberof ChatItem
     */
    set date(value) { this._date = value; }
    
    /**
     * @name messageReceiver
     * @instance
     * @property set {STRING} - Sets the message receiver of the item
     * @memberof ChatItem
     */
    set messageReceiver(value) { this._messageReceiver = value; }

    /**
     * @name message
     * @instance
     * @property get {STRING} - returns the message
     * @memberof ChatItem
     */
    get message() { return this._message; }
    /**
     * @name type
     * @instance
     * @property get {STRING} - Returns the type
     * @memberof ChatItem
     */
    get type() { return this._type; }
    /**
     * @name date
     * @instance
     * @property get {STRING} - Returns the date
     * @memberof ChatItem
     */
    get date() { return this._date; }

    /**
     * @name messageReceiver
     * @instance
     * @property get {STRING} - Returns the message receiver
     * @memberof ChatItem
     */
    get messageReceiver() { return this._messageReceiver; }

    /**
     * @name component
     * @instance
     * @property get {React.Component} - Returns the component for the chat item
     */
    get component() {
        switch(this.type) {
            case "received":
                return <ReceivedMessageWidget message={ this.message } receivedTime={ this.date } receiver={ this.messageReceiver } />
            
            case "sent":
                return <SentMessageWidget message={ this.message } sentTime={ this.date } />

            case "ended":
                return <EndedMessageWidget endTime={ this.date } />
        }
    }

    static init(message, type, date, offset, messageReceiver) {
        return new ChatItem({ message, type, date, offset, messageReceiver });
    }
}